import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";

type BreakingItem = {
  title: string;
  link: string;
  publishedAt?: string | null;
};

function toTitle(v: unknown): string {
  return String(v || "").replace(/\s+/g, " ").trim();
}

/**
 * Remembers, for the rest of the tab's life, whether the last answer was empty.
 *
 * This exists purely to protect layout stability. See the note on `reserveSpace`
 * in the component below.
 */
const EMPTY_HINT_KEY = "bullbuzz:breaking-news-empty";

function readEmptyHint(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(EMPTY_HINT_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * The breaking-news strip.
 *
 * LAYOUT-STABILITY NOTE — read before changing the render conditions.
 *
 * This component sits between `<Navbar />` and `<main>` (see Layout.tsx), so it
 * is the topmost thing on the page that can change height after the first paint.
 * It is data-gated on `/api/breaking-news`, and it used to `return null` while
 * that request was in flight. The request answers ~1.4s in, so the browser laid
 * the page out with no strip, then inserted a 45px one — pushing the hero and
 * everything under it down by 45px in a single frame. PageSpeed reported that
 * as a real cumulative layout shift, and because the strip is *above* the hero,
 * the hero `<section>` is the element that visibly moved: that is why the report
 * blamed the hero for a shift the hero had nothing to do with.
 *
 * The fix is to make the strip's height independent of whether the answer has
 * arrived yet. The outer element and the inner `h-6` row are rendered in both
 * states — an empty `h-6` row when there is nothing to show — so the box is
 * exactly 45px (1px border + 2×10px padding + 24px row) from the very first
 * frame. Content fades into space that was already reserved, and nothing below
 * the strip ever moves.
 *
 * The `sessionStorage` hint avoids paying for that reserved 45px on every
 * subsequent client-side navigation of a session in which the API answered
 * "no breaking news". A site that has breaking news — i.e. the normal case, and
 * the case this one is in — keeps the strip reserved and filled from the first
 * frame, which is what makes the shift zero.
 */
export function BreakingNewsTicker() {
  const [knownEmpty, setKnownEmpty] = useState(readEmptyHint);

  const { data, isPending } = useQuery<{ items: BreakingItem[] }>({
    queryKey: ["/api/breaking-news"],
    queryFn: async () => {
      const res = await fetch("/api/breaking-news?limit=12", { cache: "no-store" });
      if (!res.ok) return { items: [] };
      return res.json().catch(() => ({ items: [] }));
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const items = useMemo(() => {
    const raw = Array.isArray((data as any)?.items) ? (data as any).items : [];
    const cleaned = raw
      .map((a: any) => ({
        title: toTitle(a?.title),
        link: String(a?.link || "").trim(),
        ts: Date.parse(String(a?.publishedAt || "")) || 0,
      }))
      .filter((a: any) => a.title && a.link);
    cleaned.sort((a: any, b: any) => (b.ts || 0) - (a.ts || 0));
    return cleaned.slice(0, 10);
  }, [data]);

  const hasItems = items.length > 0;

  useEffect(() => {
    // Only record an answer once there is one — writing on the first render
    // would persist "empty" for a request that has not come back yet.
    if (isPending) return;
    setKnownEmpty(!hasItems);
    try {
      window.sessionStorage.setItem(EMPTY_HINT_KEY, hasItems ? "0" : "1");
    } catch {
      /* private mode / storage disabled — the hint is an optimisation only */
    }
  }, [isPending, hasItems]);

  // True until we can prove there is nothing to show.
  const reserveSpace = hasItems || (!knownEmpty && isPending);

  if (!hasItems && !reserveSpace) return null;

  const doubled = [...items, ...items];

  const isExternalHref = (href: string) => /^https?:\/\//i.test(href);

  return (
    // Height is constant in both states: border (1px) + py-2.5 (20px) + the h-6
    // row (24px) below. Only the paint changes when the data lands.
    <div
      className={
        "border-b py-2.5 " +
        (hasItems ? "bg-[#161e2e] border-white/5" : "border-transparent bg-transparent")
      }
    >
      <div className="container mx-auto px-4 flex items-center gap-4">
        {hasItems ? (
          <Badge className="bg-primary text-black font-black uppercase tracking-widest text-[10px] h-6 px-3 shrink-0">
            Breaking
          </Badge>
        ) : null}
        {/*
          `role="region"` is load-bearing: `aria-label` is not permitted on a
          bare `<div>` (a generic role), and PageSpeed's aria-prohibited-attr
          audit flags it. Naming a region is allowed and keeps the label, which
          is what tells a screen reader what the marquee is.
        */}
        <div
          role="region"
          aria-label="Breaking news"
          className="overflow-hidden relative flex-grow h-6"
        >
          {hasItems ? (
            <div className="flex gap-12 animate-breaking-marquee whitespace-nowrap items-center h-full">
              {doubled.map((a, i) => (
                <a
                  key={`${a.link}-${i}`}
                  href={a.link}
                  target={isExternalHref(a.link) ? "_blank" : undefined}
                  rel={isExternalHref(a.link) ? "noreferrer" : undefined}
                  className="text-xs font-bold text-gray-300 hover:text-primary transition-colors flex items-center gap-2"
                >
                  <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                  {a.title}
                </a>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {hasItems ? (
        <style
          dangerouslySetInnerHTML={{
            __html: `
@keyframes breakingMarquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
.animate-breaking-marquee {
  animation: breakingMarquee 42s linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  .animate-breaking-marquee { animation: none; }
}
`,
          }}
        />
      ) : null}
    </div>
  );
}
